import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { GLYPHS, AppDialog, EmptyState } from '@/components/ui';
import { Employee } from '../../types/employee';
import { HourlyRateHistory, HourlyRateUpdateType } from '../../types/hourlyRateHistory';
import { hourlyRateHistoryService } from '../../services/hourlyRateHistoryService';
import { useAccountBookStore } from '../../store/accountBookStore';
import { formatNumberWithCommas } from '../../utils/formatters';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

interface HourlyRateHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  employee: Employee;
}

const HourlyRateHistoryDialog: React.FC<HourlyRateHistoryDialogProps> = ({
  open,
  onClose,
  employee,
}) => {
  const { userId, currentAccountBook } = useAccountBookStore();
  const [histories, setHistories] = useState<HourlyRateHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    
    if (open && userId && employee && currentAccountBook) {
      loadHistory();
    } else {
      setHistories([]);
      setError(null);
    }
  }, [open, userId, employee?.id, currentAccountBook?.accountNumber]);

  const loadHistory = async () => {
    if (!userId || !employee || !currentAccountBook) {
      return;
    }


    setLoading(true);
    setError(null);

    try {
      const data = await hourlyRateHistoryService.getHistoryByEmployee(
        userId,
        employee.id,
        currentAccountBook.accountNumber
      );
      setHistories(data);
    } catch (err: any) {
      console.error('❌ HourlyRateHistoryDialog.loadHistory 오류:', err);
      setError(err.message || '시급 변경 이력을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date: Date): string => {
    return format(date, 'yyyy년 MM월 dd일 HH:mm', { locale: ko });
  };

  const getUpdateTypeLabel = (updateType: HourlyRateUpdateType): string => {
    return updateType === HourlyRateUpdateType.FULL_UPDATE ? '전체 적용' : '신규 적용';
  };

  const getUpdateTypeColor = (updateType: HourlyRateUpdateType): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    return updateType === HourlyRateUpdateType.FULL_UPDATE ? 'warning' : 'info';
  };

  return (
    <AppDialog
      open={open}
      title="시급 변경 이력"
      onClose={onClose}
      maxWidth="sm"
      actions={
        <Button onClick={onClose} variant="contained" fullWidth>
          닫기
        </Button>
      }
    >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {employee.name}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : histories.length === 0 ? (
          <EmptyState title="시급 변경 이력이 없습니다" />
        ) : (
          <List>
            {histories.map((history, index) => (
              <React.Fragment key={history.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    py: 2,
                  }}
                >
                  {/* 날짜 및 업데이트 타입 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(history.changedAt)}
                    </Typography>
                    <Chip
                      label={getUpdateTypeLabel(history.updateType)}
                      color={getUpdateTypeColor(history.updateType)}
                      size="small"
                    />
                  </Box>

                  {/* 시급 변경 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }} className="num">{formatNumberWithCommas(history.previousRate)}</Typography>
                    <Box component="span" sx={{ color: 'success.main' }}>{GLYPHS.arrow}</Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }} className="num">{formatNumberWithCommas(history.newRate)}</Typography>
                  </Box>

                  {/* 변경 사유 */}
                  {history.reason && (
                    <Box sx={{ mt: 1, width: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        변경 사유:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {history.reason}
                      </Typography>
                    </Box>
                  )}
                </ListItem>
                {index < histories.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
    </AppDialog>
  );
};

export default HourlyRateHistoryDialog;

